import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Users, Globe, Heart, Target, Lightbulb, ArrowRight } from 'lucide-react';

const Investor = () => {
  const investmentHighlights = [
    {
      icon: <Lightbulb className="h-12 w-12" />,
      title: "Innovation-Driven",
      description: "Our technology solutions address real market needs with innovative approaches that create sustainable competitive advantages.",
      metrics: "5+ Products in Development"
    },
    {
      icon: <TrendingUp className="h-12 w-12" />,
      title: "Scalable Growth",
      description: "Built with scalability at core, our business model and technology infrastructure support rapid expansion and market penetration.",
      metrics: "300% Projected Growth"
    },
    {
      icon: <Heart className="h-12 w-12" />,
      title: "Social Impact",
      description: "Our commitment to giving back 2.5% of revenue demonstrates our values-driven approach and builds strong community relationships.",
      metrics: "2.5% Revenue Commitment"
    },
    {
      icon: <Globe className="h-12 w-12" />,
      title: "Market Opportunity",
      description: "Positioned in the growing Australian tech sector with plans for regional expansion across Asia-Pacific markets.",
      metrics: "Billion $ Market"
    }
  ];

  const keyMetrics = [
    { number: '2025', label: 'Founded', description: 'Established with clear vision' },
    { number: '5+', label: 'Products', description: 'In development pipeline' },
    { number: '2.5%', label: 'Revenue', description: 'Dedicated to social impact' },
    { number: '100%', label: 'Remote-First', description: 'Global talent access' }
  ];

  const investmentAreas = [
    {
      title: "Product Development",
      description: "Accelerating development of our flagship products including GoFella and upcoming platform solutions.",
      allocation: "40%"
    },
    {
      title: "Market Expansion",
      description: "Growing our presence across Australia and expanding into key Asia-Pacific markets.",
      allocation: "25%"
    },
    {
      title: "Team Growth",
      description: "Attracting top-tier talent across engineering, product, sales, and marketing functions.",
      allocation: "20%"
    },
    {
      title: "Technology Infrastructure",
      description: "Building robust, scalable infrastructure to support rapid growth and innovation.",
      allocation: "15%"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Invest in the{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Future
              </span>{' '}
              of Technology
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Join us in building the next generation of technology solutions that empower people, 
              drive growth, and create positive social impact. Discover why Vincotek represents 
              an exceptional investment opportunity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/investor/overview">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Investment Overview
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Contact Our Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                  Vincotek's Growth Story
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Founded in 2025 with a mission to democratize technology and create positive social impact, 
                  Vincotek has quickly established itself as an innovative force in the Australian tech landscape.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our unique approach combines cutting-edge technology development with a strong commitment to 
                  social responsibility, creating a business model that drives both financial returns and meaningful impact.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-vincotek-dark">Why Invest Now?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">Early-stage opportunity in fast-growing tech sector</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">Proven leadership team with track record of success</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">Multiple revenue streams and scalable business model</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">Strong values alignment with ESG investment principles</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab" 
                alt="Growth and innovation" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                <div className="text-vincotek-dark text-2xl font-bold">2025</div>
                <div className="text-gray-600 text-sm">The Journey Begins</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Key Investment Metrics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our strong fundamentals and growth trajectory position us for exceptional returns.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <Card key={index} className="text-center bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-vincotek-blue mb-2">
                    {metric.number}
                  </div>
                  <div className="text-lg font-semibold text-vincotek-dark mb-2">
                    {metric.label}
                  </div>
                  <p className="text-sm text-gray-600">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Highlights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Why Invest in Vincotek
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four key pillars that make Vincotek an exceptional investment opportunity.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {investmentHighlights.map((highlight, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="text-vincotek-blue group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      {highlight.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-vincotek-dark mb-3">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {highlight.description}
                      </p>
                      <div className="inline-block bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-4 py-2 rounded-full text-sm font-medium">
                        {highlight.metrics}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Areas */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Investment Allocation
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Strategic deployment of investment capital across key growth areas.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {investmentAreas.map((area, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-white">
                      {area.title}
                    </h3>
                    <span className="text-2xl font-bold text-vincotek-yellow">
                      {area.allocation}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    {area.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced leaders with proven track records in technology, business growth, and value creation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-xl">K</span>
                </div>
                <h3 className="text-xl font-semibold text-vincotek-dark mb-2">
                  Keyvin Runtuwarouw
                </h3>
                <p className="text-vincotek-blue font-medium mb-4">
                  Chief Executive Officer
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Visionary leader with extensive experience in technology innovation and business strategy.
                </p>
                <Link to="/about/leadership/keyvin-runtuwarouw">
                  <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300">
                    View Profile
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
                <h3 className="text-xl font-semibold text-vincotek-dark mb-2">
                  Rey Runtuwarouw
                </h3>
                <p className="text-vincotek-blue font-medium mb-4">
                  Chief Corporate Officer
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Strategic executive focused on corporate development and organizational excellence.
                </p>
                <Link to="/about/leadership/Rey-Runtuwarouw">
                  <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300">
                    View Profile
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-4">
            Ready to Join Our Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Connect with our team to learn more about investment opportunities and how you can 
            be part of Vincotek's growth story.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg group">
                Contact Our Team
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white px-8 py-3 rounded-full transition-all duration-300">
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investor;